import React, { useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { accentColors } from "../../../providers/ThemeProvider";

import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import FormatColorTextOutlinedIcon from "@mui/icons-material/FormatColorTextOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import BrightnessMediumOutlinedIcon from "@mui/icons-material/BrightnessMediumOutlined";

interface ThemeSettingProps {
  themeMode: string;
  setThemeMode: (newMode: "light" | "dark" | "auto") => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
}

const ThemeSettings: React.FC<ThemeSettingProps> = ({
  themeMode,
  setThemeMode,
  accentColor,
  setAccentColor,
}) => {
  const theme = useTheme();

  type Theme = {
    type: "light" | "dark" | "auto";
    icon: JSX.Element;
    title: string;
  };

  const themes: Theme[] = [
    {
      type: "light",
      icon: <LightModeOutlinedIcon sx={{ fontSize: 14 }} />,
      title: "โหมดสว่าง",
    },
    {
      type: "dark",
      icon: <DarkModeOutlinedIcon sx={{ fontSize: 14 }} />,
      title: "โหมดมืด",
    },
    {
      type: "auto",
      icon: <BrightnessMediumOutlinedIcon sx={{ fontSize: 14 }} />,
      title: "ค่าเริ่มต้นระบบ",
    },
  ];

  const handleThemeChange = (mode: "light" | "dark" | "auto") => {
    setThemeMode(mode);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Title */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 4,
          pb: 8,
          width: "100%",
          maxWidth: 800,
          margin: "auto",
          position: "relative",
        }}
      >
        <ColorLensOutlinedIcon
          sx={{ fontSize: 72, mb: 4, color: "primary.main" }}
        />
        <Typography
          variant="h5"
          color="textPrimary"
          sx={{
            position: "relative",
            textAlign: "center",
            mb: 1,
            zIndex: 1,
            fontWeight: 600,
          }}
        >
          ตั้งค่าธีม
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ position: "relative", textAlign: "center", mb: 1, zIndex: 1 }}
        >
          ตั้งค่าสี Accent และโหมดสีพื้นหลัง
        </Typography>
      </Box>

      {/* Information */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: 2,
          borderRadius: 2,
          width: "100%",
          maxWidth: 800,
          mb: 4,
          gap: 2,
          background:
            "linear-gradient(135deg, rgba(168, 192, 255, 0.16), rgba(252, 194, 215, 0.16))",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.primary" }} />
        <Typography variant="body2" color="textPrimary">
          ผู้ใช้งานสามารถปรับแต่งธีมหลักของระบบได้ รวมถึงการตั้งค่าสี Accent
          และโหมดสีพื้นหลัง (เช่น โหมดสว่าง โหมดมืด หรือ
          โหมดอัตโนมัติตามระบบของผู้ใช้งาน) เพื่อให้เหมาะกับการใช้งานมากยิ่งขึ้น
        </Typography>
      </Box>

      {/* Theme Settings */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          padding: 2,
          borderRadius: 2,
          width: "100%",
          maxWidth: 800,
          border: "1px solid",
          borderColor: "divider",
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <NightsStayOutlinedIcon
            sx={{ fontSize: 32, color: "primary.main", mr: 2 }}
          />
          <Box flexGrow={1}>
            <Typography
              variant="body1"
              color="textPrimary"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              ตั้งค่าธีม
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ผู้ใช้งานสามารถปรับแต่งธีมหลักของระบบได้
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            ml: 6,
            mt: 2,
            gap: 2,
            width: `calc(100% - 48px)`,
            "@media (max-width:600px)": { flexDirection: "column" },
          }}
        >
          {themes.map((theme) => (
            <Button
              key={theme.type}
              variant="outlined"
              onClick={() => handleThemeChange(theme.type)}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                width: 160,
                color:
                  theme.type !== themeMode ? "text.primary" : "primary.main",
                borderColor:
                  theme.type !== themeMode ? "divider" : "primary.main",
                "@media (max-width:600px)": {
                  width: "100%",
                },
              }}
            >
              {theme.icon}
              {theme.title}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Accent Color Settings */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          padding: 2,
          borderRadius: 2,
          width: "100%",
          maxWidth: 800,
          border: "1px solid",
          borderColor: "divider",
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <FormatColorTextOutlinedIcon
            sx={{ fontSize: 32, color: "primary.main", mr: 2 }}
          />
          <Box flexGrow={1}>
            <Typography
              variant="body1"
              color="textPrimary"
              sx={{ fontWeight: 600, mb: 0.5 }}
            >
              ตั้งค่าสี Accent
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ผู้ใช้งานสามารถปรับแต่งธีมหลักของระบบได้
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", ml: 6, mt: 2, gap: 2, flexWrap: "wrap" }}>
          {Object.entries(accentColors).map(([colorKey, color]) => (
            <Button
              key={colorKey}
              variant="outlined"
              onClick={() => setAccentColor(colorKey)}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                gap: 1,
                width: 160,
                color:
                  colorKey !== accentColor ? "text.primary" : "primary.main",
                borderColor:
                  colorKey !== accentColor ? "divider" : "primary.main",
                "@media (max-width:600px)": {
                  width: `calc(50% - 8px)`,
                },
                "@media (max-width:400px)": {
                  width: "100%",
                },
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 1,
                  backgroundColor:
                    theme.palette.mode === "dark" ? color.dark : color.light,
                }}
              ></Box>
              สี{color.title}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ThemeSettings;
